import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { dealsApi } from "@/api/endpoints";
import type { DealScope } from "@/api/types";
import { useAuth } from "@/auth/use-auth";
import { Button } from "@/components/ui/Button";
import { Note } from "@/components/ui/Note";
import { StatusPill } from "@/components/ui/Pill";
import { SearchInput } from "@/components/ui/SearchInput";
import type { MsgKey } from "@/i18n/messages";
import { useI18n } from "@/i18n/use-i18n";
import { cn } from "@/lib/cn";

const FILTERS: DealScope[] = ["all", "internal", "agency", "mine", "open", "done"];

const COLUMNS: MsgKey[] = [
  "dashboard.col.reference",
  "dashboard.col.customer",
  "dashboard.col.product",
  "dashboard.col.owner",
  "dashboard.col.created",
  "dashboard.col.value",
  "dashboard.col.status",
];

export function DashboardPage() {
  const { user } = useAuth();
  const { t, formatDate } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [scope, setScope] = useState<DealScope>("all");
  const [search, setSearch] = useState("");

  const stats = useQuery({ queryKey: ["deal-stats"], queryFn: dealsApi.stats });
  const deals = useQuery({
    queryKey: ["deals", scope, search],
    queryFn: () => dealsApi.list(scope, search),
  });

  const createDeal = useMutation({
    mutationFn: dealsApi.create,
    onSuccess: (deal) => {
      void queryClient.invalidateQueries({ queryKey: ["deals"] });
      void queryClient.invalidateQueries({ queryKey: ["deal-stats"] });
      navigate(`/vorgang/${deal.id}`);
    },
  });

  const firstName = user?.name.split(" ")[0] ?? "";

  return (
    <div className="page-shell pt-6.5 pb-17">
      <div className="mb-6 flex flex-wrap items-start gap-4">
        <div>
          <h1 className="text-[23px] font-bold sm:text-[28px]">
            {t("dashboard.greeting", { name: firstName })}
          </h1>
          <p className="mt-1 text-[14.5px] text-muted">{t("dashboard.intro")}</p>
        </div>
        {/* Full width on a phone, where ml-auto would otherwise orphan it on a
            wrapped row of its own. */}
        <Button
          className="w-full sm:ml-auto sm:w-auto"
          busy={createDeal.isPending}
          onClick={() => createDeal.mutate()}
        >
          {t("dashboard.newDeal")}
        </Button>
      </div>

      {createDeal.isError && (
        <Note tone="error" className="mb-4">
          {t("dashboard.createFailed", { message: (createDeal.error as Error).message })}
        </Note>
      )}

      {/* KPIs */}
      <div className="mb-6 grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          label={t("dashboard.kpi.total")}
          value={stats.data ? String(stats.data.total) : "—"}
          hint={stats.data ? t("dashboard.kpi.totalHint", { count: stats.data.mine }) : ""}
        />
        <Kpi
          label={t("dashboard.kpi.open")}
          value={stats.data ? String(stats.data.open) : "—"}
          hint={t("dashboard.kpi.openHint")}
        />
        <Kpi
          label={t("dashboard.kpi.completed")}
          value={stats.data ? String(stats.data.completed) : "—"}
          hint={t("dashboard.kpi.completedHint")}
          accent
        />
        <Kpi
          label={t("dashboard.kpi.volume")}
          // German-formatted from the backend, like every other money figure.
          value={stats.data?.completed_volume_label ?? "—"}
          hint={t("dashboard.kpi.volumeHint")}
        />
      </div>

      {/* Scrolls rather than wrapping on a phone: six pills would otherwise
          stack into three rows above the list. */}
      <div className="-mx-4 mb-4 flex gap-2 overflow-x-auto px-4 sm:mx-0 sm:flex-wrap sm:px-0">
        {FILTERS.map((filter) => (
          <button
            key={filter}
            type="button"
            aria-pressed={scope === filter}
            onClick={() => setScope(filter)}
            className={cn(
              "min-h-11 shrink-0 rounded-full border px-3.5 text-[13px] font-medium transition-colors sm:min-h-0 sm:py-1.5",
              scope === filter
                ? "border-navy bg-navy text-white"
                : "border-line bg-surface text-muted hover:bg-line-soft",
            )}
          >
            {t(`dashboard.filter.${filter}`)}
          </button>
        ))}
      </div>

      <SearchInput
        label={t("dashboard.search")}
        value={search}
        onChange={setSearch}
        placeholder={t("dashboard.searchPlaceholder")}
        className="mb-4 max-w-[420px]"
      />

      {/* Phones get cards; the table needs 7 columns and cannot honestly fit. */}
      <div className="grid gap-2.5 md:hidden">
        {deals.isPending && <p className="py-6 text-center text-muted">{t("dashboard.loading")}</p>}
        {deals.isError && (
          <p className="py-6 text-center text-danger">{(deals.error as Error).message}</p>
        )}
        {deals.data?.length === 0 && (
          <p className="py-6 text-center text-muted">{t("dashboard.empty")}</p>
        )}
        {deals.data?.map((deal) => (
          <button
            key={deal.id}
            type="button"
            onClick={() => navigate(`/vorgang/${deal.id}`)}
            className="w-full rounded-card bg-surface px-4 py-3.5 text-left shadow-card"
          >
            <div className="flex items-start justify-between gap-3">
              <span className="font-semibold">{deal.customer_name ?? "—"}</span>
              <StatusPill bucket={deal.bucket} />
            </div>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-2.5 gap-y-1 text-[13px] text-muted">
              <span className="font-mono text-[12.5px]">{deal.reference}</span>
              <span className="font-mono text-[12.5px] text-ink">{deal.total_label || "—"}</span>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-2.5 gap-y-1 text-[12.5px] text-muted">
              <span>{deal.product_name ?? "—"}</span>
              <span>·</span>
              <span>{deal.owner.name}</span>
              <span>·</span>
              <span>{formatDate(deal.created_at)}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Table, from tablet up. min-w so it scrolls cleanly instead of wrapping
          every column down to min-content first. */}
      <div className="hidden overflow-x-auto rounded-card bg-surface shadow-card md:block">
        {/* 860px is above the seven columns' combined min-content width, so the
            table scrolls as one clean unit instead of wrapping every cell down
            to its longest word first. It fits without scrolling from lg up. */}
        <table className="w-full min-w-[860px] border-collapse text-[14px]">
          <thead>
            <tr className="border-b border-line text-left">
              {COLUMNS.map((key) => (
                <th
                  key={key}
                  scope="col"
                  className="px-4 py-3 text-[11.5px] font-semibold tracking-[0.08em] text-muted uppercase"
                >
                  {t(key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {deals.isPending && (
              <tr>
                <td colSpan={7} className="px-4 py-9 text-center text-muted">
                  {t("dashboard.loading")}
                </td>
              </tr>
            )}

            {deals.isError && (
              <tr>
                <td colSpan={7} className="px-4 py-9 text-center text-danger">
                  {(deals.error as Error).message}
                </td>
              </tr>
            )}

            {deals.data?.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-9 text-center text-muted">
                  {t("dashboard.empty")}
                </td>
              </tr>
            )}

            {deals.data?.map((deal) => (
              <tr
                key={deal.id}
                tabIndex={0}
                role="link"
                onClick={() => navigate(`/vorgang/${deal.id}`)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    navigate(`/vorgang/${deal.id}`);
                  }
                }}
                className="cursor-pointer border-b border-line-soft transition-colors last:border-0 hover:bg-line-soft/60"
              >
                <td className="px-4 py-3 font-mono text-[12.5px]">{deal.reference}</td>
                <td className="px-4 py-3 font-semibold">{deal.customer_name ?? "—"}</td>
                <td className="px-4 py-3">{deal.product_name ?? "—"}</td>
                <td className="px-4 py-3">{deal.owner.name}</td>
                <td className="px-4 py-3">{formatDate(deal.created_at)}</td>
                {/* total_label, not amount_cents: the latter is the net figure,
                    while this column shows the gross the customer is charged.
                    It arrives German-formatted and stays that way. */}
                <td className="px-4 py-3 font-mono text-[12.5px]">{deal.total_label || "—"}</td>
                <td className="px-4 py-3">
                  <StatusPill bucket={deal.bucket} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface KpiProps {
  label: string;
  value: string;
  hint: string;
  accent?: boolean;
}

function Kpi({ label, value, hint, accent = false }: KpiProps) {
  return (
    <div
      className={cn(
        "rounded-card bg-surface px-4.5 py-4 shadow-card",
        accent && "border-l-[3px] border-teal",
      )}
    >
      <div className="text-[11px] font-semibold tracking-[0.09em] text-muted uppercase">
        {label}
      </div>
      <div className="mt-1.5 font-display text-[26px] leading-none font-bold">{value}</div>
      <div className="mt-1.5 text-[12px] text-muted">{hint}</div>
    </div>
  );
}
